import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { movimientosStockService, productosService, almacenesService, inventarioService } from '../../services/tiendaService';
import { useAuth } from '../../contexts/AuthContext';

const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const ORANGE = '#FB923C';
const RED = '#F87171';

// Tipos exactos admitidos por el CHECK constraint de tienda.movimientos_stock
const TIPOS_MOVIMIENTO = ['ENTRADA', 'VENTA', 'MERMA', 'AJUSTE', 'DEVOLUCION'];

const TIPO_CONFIG = {
  ENTRADA:   { label: 'Entrada',     color: ACCENT,   icon: 'ArrowDownCircle', dir: 'entrada' },
  VENTA:     { label: 'Venta',       color: RED,      icon: 'TrendingDown',    dir: 'salida'  },
  MERMA:     { label: 'Merma',       color: ORANGE,   icon: 'Trash2',          dir: 'salida'  },
  AJUSTE:    { label: 'Ajuste',      color: BLUE,     icon: 'SlidersHorizontal', dir: 'ajuste' },
  DEVOLUCION:{ label: 'Devolución',  color: '#A78BFA', icon: 'RotateCcw',      dir: 'entrada' },
};

const fmt  = (n, d = 2) => typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
const fmtEur = (n) => typeof n === 'number' ? `${fmt(n)} €` : '—';
const fmtDate = (s) => s ? new Date(s)?.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// ─── BADGE TIPO ───────────────────────────────────────────────────────────────
const TipoBadge = ({ tipo }) => {
  const cfg = TIPO_CONFIG?.[tipo] || { label: tipo, color: GOLD, icon: 'Circle' };
  return (
    <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-oranienbaum whitespace-nowrap"
      style={{ background: `${cfg?.color}15`, color: cfg?.color, border: `1px solid ${cfg?.color}30` }}>
      <Icon name={cfg?.icon} size={10} />
      {cfg?.label}
    </span>
  );
};

// ─── CAMPO FORMULARIO ─────────────────────────────────────────────────────────
const Field = ({ label, required = false, children, hint = null }) => (
  <div>
    <label className="block font-oranienbaum text-white/50 text-xs mb-1">
      {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      {hint && <span className="ml-1 text-white/30 text-[10px]">({hint})</span>}
    </label>
    {children}
  </div>
);

// ─── MODAL NUEVO / EDITAR MOVIMIENTO ─────────────────────────────────────────
const MovimientoFormModal = ({ productos, almacenes, inventario, productoPreseleccionado, onSave, onClose }) => {
  const { user, userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'administrator';

  const [form, setForm] = useState({
    producto_id: productoPreseleccionado || '',
    lote_id: '',
    tipo_movimiento: 'ENTRADA',
    cantidad: '',
    coste_unitario: '',
    almacen_origen_id: '',
    almacen_destino_id: '',
    referencia_tipo: '',
    referencia_id: '',
    observaciones: '',
    usuario: userProfile?.full_name || user?.email || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const tipo = form?.tipo_movimiento;
  const cfg  = TIPO_CONFIG?.[tipo] || { color: GOLD };

  // Stock disponible del producto+almacén seleccionado
  const stockDisponible = useCallback(() => {
    if (!form?.producto_id) return null;
    const almacenId = form?.almacen_origen_id || null;
    const rows = inventario?.filter(i => i?.producto_id === form?.producto_id);
    if (rows?.length === 0) return null;
    if (almacenId) {
      const row = rows?.find(i => i?.almacen_id === almacenId);
      return row ? (row?.stock_disponible ?? row?.stock_actual ?? 0) : 0;
    }
    // Sin almacén específico: suma total
    return rows?.reduce((s, i) => s + (i?.stock_disponible ?? i?.stock_actual ?? 0), 0);
  }, [form?.producto_id, form?.almacen_origen_id, inventario]);

  const validate = () => {
    if (!form?.producto_id)   return 'Selecciona un producto.';
    if (!form?.tipo_movimiento) return 'Selecciona el tipo de movimiento.';

    const qty = parseFloat(form?.cantidad);

    // AJUSTE permite positivo o negativo, pero no 0
    if (tipo === 'AJUSTE') {
      if (!form?.cantidad || qty === 0) return 'La cantidad del ajuste no puede ser 0.';
    } else {
      if (!form?.cantidad || isNaN(qty) || qty === 0) return 'La cantidad debe ser distinta de 0.';
    }

    // Reglas de almacén por tipo
    if ((tipo === 'ENTRADA' || tipo === 'DEVOLUCION') && !form?.almacen_destino_id)
      return `Para ${TIPO_CONFIG?.[tipo]?.label} es obligatorio indicar el almacén destino.`;
    if ((tipo === 'VENTA' || tipo === 'MERMA') && !form?.almacen_origen_id)
      return `Para ${TIPO_CONFIG?.[tipo]?.label} es obligatorio indicar el almacén origen.`;

    // Coste unitario obligatorio en ENTRADA
    if (tipo === 'ENTRADA' && (form?.coste_unitario === '' || form?.coste_unitario === null))
      return 'El coste unitario es obligatorio para una Entrada.';

    // Validar stock disponible para salidas
    if (['VENTA', 'MERMA']?.includes(tipo)) {
      const stock = stockDisponible();
      if (stock !== null && qty > stock)
        return `Stock insuficiente. Disponible: ${fmt(stock, 3)} uds.`;
    }
    // AJUSTE negativo: también validar stock
    if (tipo === 'AJUSTE' && qty < 0) {
      const stock = stockDisponible();
      if (stock !== null && Math.abs(qty) > stock)
        return `Ajuste negativo supera el stock disponible (${fmt(stock, 3)} uds.).`;
    }

    return null;
  };

  const handleSave = async () => {
    const validationErr = validate();
    if (validationErr) { setErr(validationErr); return; }
    try {
      setSaving(true);
      setErr(null);
      const qty = parseFloat(form?.cantidad);
      const payload = {
        producto_id:       form?.producto_id,
        lote_id:           form?.lote_id || null,
        tipo_movimiento:   form?.tipo_movimiento,
        cantidad:          qty,
        coste_unitario:    form?.coste_unitario !== '' ? parseFloat(form?.coste_unitario) : null,
        almacen_origen_id: form?.almacen_origen_id  || null,
        almacen_destino_id:form?.almacen_destino_id || null,
        referencia_tipo:   form?.referencia_tipo    || null,
        referencia_id:     form?.referencia_id      || null,
        observaciones:     form?.observaciones      || null,
        usuario:           form?.usuario            || null,
      };
      await onSave(payload);
      onClose();
    } catch (e) {
      // Mostrar error real de Supabase
      setErr(e?.message || 'Error al registrar movimiento');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none";
  const inputSt  = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };
  const selectSt = { background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.85)' };

  const stockInfo = stockDisponible();
  const showStockWarning = ['VENTA', 'MERMA', 'AJUSTE']?.includes(tipo) && stockInfo !== null;

  // Reglas de visibilidad de almacenes
  const showOrigen  = tipo === 'VENTA' || tipo === 'MERMA' || tipo === 'AJUSTE';
  const showDestino = tipo === 'ENTRADA' || tipo === 'DEVOLUCION' || tipo === 'AJUSTE';
  const origenReq   = tipo === 'VENTA' || tipo === 'MERMA';
  const destinoReq  = tipo === 'ENTRADA' || tipo === 'DEVOLUCION';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.82)' }}>
      <div className="w-full max-w-lg rounded-2xl flex flex-col max-h-[92vh]" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${cfg?.color}15`, border: `1px solid ${cfg?.color}30` }}>
              <Icon name="ArrowUpDown" size={15} style={{ color: cfg?.color }} />
            </div>
            <h3 className="font-oranienbaum font-bold text-white text-lg">Nuevo Movimiento</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 transition-colors"><Icon name="X" size={16} /></button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-3">
          {err && (
            <div className="p-3 rounded-xl text-xs font-oranienbaum text-red-400 flex items-start gap-2"
              style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              <Icon name="AlertCircle" size={13} className="flex-shrink-0 mt-0.5" />
              {err}
            </div>
          )}

          {/* Tipo */}
          <Field label="Tipo de Movimiento" required>
            <select value={form?.tipo_movimiento} onChange={e => { set('tipo_movimiento', e?.target?.value); setErr(null); }}
              className={inputCls} style={selectSt}>
              {TIPOS_MOVIMIENTO?.map(t => <option key={t} value={t}>{TIPO_CONFIG?.[t]?.label}</option>)}
            </select>
          </Field>

          {/* Info tipo */}
          <div className="px-3 py-2 rounded-xl text-xs font-oranienbaum" style={{ background: `${cfg?.color}08`, border: `1px solid ${cfg?.color}15`, color: `${cfg?.color}CC` }}>
            {tipo === 'ENTRADA'    && 'Entrada de mercancía. Requiere almacén destino y coste unitario.'}
            {tipo === 'VENTA'      && 'Salida por venta. Requiere almacén origen. No puede superar el stock disponible.'}
            {tipo === 'MERMA'      && 'Pérdida o deterioro. Requiere almacén origen. No puede superar el stock disponible.'}
            {tipo === 'AJUSTE'     && 'Corrección de inventario. Selecciona almacén. Cantidad positiva (incremento) o negativa (reducción).'}
            {tipo === 'DEVOLUCION' && 'Devolución de producto. Requiere almacén destino.'}
          </div>

          {/* Producto */}
          <Field label="Producto" required>
            <select value={form?.producto_id} onChange={e => { set('producto_id', e?.target?.value); setErr(null); }}
              className={inputCls} style={selectSt}>
              <option value="">Seleccionar producto...</option>
              {productos?.map(p => <option key={p?.id} value={p?.id}>{p?.nombre}{p?.codigo_interno ? ` (${p?.codigo_interno})` : ''}</option>)}
            </select>
          </Field>

          {/* Stock disponible */}
          {showStockWarning && form?.producto_id && (
            <div className="px-3 py-2 rounded-xl text-xs font-oranienbaum flex items-center gap-2"
              style={{ background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.20)', color: '#60A5FA' }}>
              <Icon name="Info" size={12} />
              Stock disponible: <strong>{stockInfo !== null ? fmt(stockInfo, 3) : '—'} uds.</strong>
            </div>
          )}

          {/* Cantidad */}
          <Field label="Cantidad" required hint={tipo === 'AJUSTE' ? 'positivo o negativo' : undefined}>
            <input type="number"
              step="0.001"
              value={form?.cantidad}
              onChange={e => { set('cantidad', e?.target?.value); setErr(null); }}
              className={inputCls} style={inputSt}
              placeholder={tipo === 'AJUSTE' ? 'ej: 5 o -3' : '0.000'} />
          </Field>

          {/* Coste unitario */}
          <Field label="Coste Unitario (€)" required={tipo === 'ENTRADA'} hint={tipo !== 'ENTRADA' ? 'opcional' : undefined}>
            <input type="number" min="0" step="0.0001"
              value={form?.coste_unitario}
              onChange={e => set('coste_unitario', e?.target?.value)}
              className={inputCls} style={inputSt}
              placeholder={tipo === 'ENTRADA' ? 'Obligatorio' : 'Opcional'} />
          </Field>

          {/* Almacenes según tipo */}
          <div className={`grid gap-3 ${showOrigen && showDestino ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {showOrigen && (
              <Field label="Almacén Origen" required={origenReq}>
                <select value={form?.almacen_origen_id} onChange={e => { set('almacen_origen_id', e?.target?.value); setErr(null); }}
                  className={inputCls} style={selectSt}>
                  <option value="">{origenReq ? 'Seleccionar...' : 'Sin especificar'}</option>
                  {almacenes?.map(a => <option key={a?.id} value={a?.id}>{a?.nombre}</option>)}
                </select>
              </Field>
            )}
            {showDestino && (
              <Field label="Almacén Destino" required={destinoReq}>
                <select value={form?.almacen_destino_id} onChange={e => { set('almacen_destino_id', e?.target?.value); setErr(null); }}
                  className={inputCls} style={selectSt}>
                  <option value="">{destinoReq ? 'Seleccionar...' : 'Sin especificar'}</option>
                  {almacenes?.map(a => <option key={a?.id} value={a?.id}>{a?.nombre}</option>)}
                </select>
              </Field>
            )}
          </div>

          {/* Lote */}
          <Field label="Lote (opcional)">
            <input type="text" value={form?.lote_id} onChange={e => set('lote_id', e?.target?.value)}
              className={inputCls} style={inputSt} placeholder="ID de lote..." />
          </Field>

          {/* Referencia */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo Referencia">
              <input type="text" value={form?.referencia_tipo} onChange={e => set('referencia_tipo', e?.target?.value)}
                className={inputCls} style={inputSt} placeholder="factura, pedido..." />
            </Field>
            <Field label="ID Referencia">
              <input type="text" value={form?.referencia_id} onChange={e => set('referencia_id', e?.target?.value)}
                className={inputCls} style={inputSt} placeholder="Número o UUID" />
            </Field>
          </div>

          {/* Usuario */}
          <Field label="Usuario">
            <input type="text" value={form?.usuario} onChange={e => set('usuario', e?.target?.value)}
              className={inputCls} style={inputSt} placeholder="Nombre del operario" />
          </Field>

          {/* Observaciones */}
          <Field label="Observaciones">
            <textarea value={form?.observaciones} onChange={e => set('observaciones', e?.target?.value)}
              rows={2} className={inputCls} style={{ ...inputSt, resize: 'none' }} placeholder="Opcional..." />
          </Field>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum disabled:opacity-50 transition-all"
            style={{ background: `${cfg?.color}20`, border: `1px solid ${cfg?.color}40`, color: cfg?.color }}>
            {saving ? 'Guardando...' : 'Registrar Movimiento'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MODAL DETALLE ────────────────────────────────────────────────────────────
const MovimientoDetailModal = ({ mov, onClose }) => {
  const cfg = TIPO_CONFIG?.[mov?.tipo_movimiento] || { label: mov?.tipo_movimiento, color: GOLD, icon: 'Circle' };
  const Row = ({ label, val }) => (
    <div className="flex justify-between items-start py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <span className="font-oranienbaum text-white/40 text-xs w-36 flex-shrink-0">{label}</span>
      <span className="font-oranienbaum text-white/80 text-xs text-right">{val || <span className="text-white/25">—</span>}</span>
    </div>
  );

  // Parse factura reference from observaciones
  const facturaRef = mov?.observaciones?.match(/factura\s+([^\s—]+)/i)?.[1] || null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.82)' }}>
      <div className="w-full max-w-md rounded-2xl flex flex-col max-h-[88vh]" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${cfg?.color}15`, border: `1px solid ${cfg?.color}30` }}>
              <Icon name={cfg?.icon} size={15} style={{ color: cfg?.color }} />
            </div>
            <div>
              <h3 className="font-oranienbaum font-bold text-white text-base">{cfg?.label}</h3>
              <p className="font-oranienbaum text-white/35 text-xs">{fmtDate(mov?.created_at)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 transition-colors"><Icon name="X" size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">
          <Row label="Producto"        val={mov?.producto?.nombre} />
          <Row label="Código"          val={mov?.producto?.codigo_interno} />
          <Row label="Tipo"            val={cfg?.label} />
          <Row label="Cantidad"        val={fmt(mov?.cantidad, 3)} />
          <Row label="Coste Unitario"  val={fmtEur(mov?.coste_unitario)} />
          <Row label="Coste Total"     val={fmtEur(typeof mov?.coste_unitario === 'number' && typeof mov?.cantidad === 'number' ? mov?.coste_unitario * mov?.cantidad : null)} />
          <Row label="Almacén Origen"  val={mov?.almacen_origen?.nombre} />
          <Row label="Almacén Destino" val={mov?.almacen_destino?.nombre} />
          <Row label="Lote"            val={mov?.lote_id} />
          {mov?.referencia_tipo === 'FACTURA_COMPRA' && (
            <Row label="Factura compra" val={facturaRef || mov?.referencia_id?.substring(0, 8)} />
          )}
          <Row label="Ref. Tipo"       val={mov?.referencia_tipo} />
          <Row label="Ref. ID"         val={mov?.referencia_id} />
          <Row label="Usuario"         val={mov?.usuario} />
          <Row label="Observaciones"   val={mov?.observaciones} />
        </div>
        <div className="px-6 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="w-full py-2.5 rounded-xl text-sm font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MODAL EDITAR OBSERVACIONES ───────────────────────────────────────────────
const EditObservacionesModal = ({ mov, onSave, onClose }) => {
  const [obs, setObs] = useState(mov?.observaciones || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const handleSave = async () => {
    try {
      setSaving(true);
      setErr(null);
      await onSave(mov?.id, obs);
      onClose();
    } catch (e) {
      setErr(e?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.82)' }}>
      <div className="w-full max-w-md rounded-2xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.25)' }}>
              <Icon name="FileEdit" size={15} style={{ color: BLUE }} />
            </div>
            <h3 className="font-oranienbaum font-bold text-white text-base">Editar Observaciones</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 transition-colors"><Icon name="X" size={16} /></button>
        </div>
        <div className="px-6 py-4 space-y-3">
          {err && (
            <div className="p-3 rounded-xl text-xs font-oranienbaum text-red-400"
              style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              {err}
            </div>
          )}
          <div className="font-oranienbaum text-white/40 text-xs mb-1">
            Movimiento: <span className="text-white/60">{TIPO_CONFIG?.[mov?.tipo_movimiento]?.label} — {mov?.producto?.nombre}</span>
          </div>
          <textarea
            value={obs}
            onChange={e => setObs(e?.target?.value)}
            rows={4}
            className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', resize: 'none' }}
            placeholder="Observaciones..."
          />
        </div>
        <div className="flex gap-3 px-6 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum disabled:opacity-50"
            style={{ background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.30)', color: BLUE }}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MODAL CONFIRMAR ELIMINAR ─────────────────────────────────────────────────
const ConfirmDeleteModal = ({ mov, onConfirm, onClose }) => {
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState(null);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setErr(null);
      await onConfirm(mov?.id);
      onClose();
    } catch (e) {
      setErr(e?.message || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.82)' }}>
      <div className="w-full max-w-sm rounded-2xl" style={{ background: '#141414', border: '1px solid rgba(248,113,113,0.25)' }}>
        <div className="px-6 py-5 text-center">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'rgba(248,113,113,0.10)', border: '1px solid rgba(248,113,113,0.25)' }}>
            <Icon name="Trash2" size={22} style={{ color: RED }} />
          </div>
          <h3 className="font-oranienbaum font-bold text-white text-lg mb-2">Eliminar Movimiento</h3>
          <p className="font-oranienbaum text-white/50 text-sm mb-1">
            {TIPO_CONFIG?.[mov?.tipo_movimiento]?.label} — {mov?.producto?.nombre}
          </p>
          <p className="font-oranienbaum text-white/35 text-xs mb-4">
            Cantidad: {fmt(mov?.cantidad, 3)} · {fmtDate(mov?.created_at)}
          </p>
          <div className="p-3 rounded-xl text-xs font-oranienbaum text-orange-400 mb-4"
            style={{ background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.20)' }}>
            ⚠️ Esta acción no puede deshacerse. El inventario no se revertirá automáticamente.
          </div>
          {err && (
            <div className="p-3 rounded-xl text-xs font-oranienbaum text-red-400 mb-3"
              style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              {err}
            </div>
          )}
        </div>
        <div className="flex gap-3 px-6 pb-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            Cancelar
          </button>
          <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum disabled:opacity-50"
            style={{ background: 'rgba(248,113,113,0.15)', border: '1px solid rgba(248,113,113,0.30)', color: RED }}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── PÁGINA PRINCIPAL ─────────────────────────────────────────────────────────
const TiendaMovimientos = () => {
  const location = useLocation();
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'administrator';

  const queryParams = new URLSearchParams(location?.search);
  const productoParam = queryParams?.get('producto') || '';

  const [movimientos, setMovimientos] = useState([]);
  const [productos,   setProductos]   = useState([]);
  const [almacenes,   setAlmacenes]   = useState([]);
  const [inventario,  setInventario]  = useState([]);
  const [kpis,        setKpis]        = useState({});
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  // Filtros — se mantienen tras guardar
  const [search,         setSearch]         = useState('');
  const [filterProducto, setFilterProducto] = useState(productoParam);
  const [filterTipo,     setFilterTipo]     = useState('');
  const [filterAlmacen,  setFilterAlmacen]  = useState('');
  const [filterDesde,    setFilterDesde]    = useState('');
  const [filterHasta,    setFilterHasta]    = useState('');
  const [filterOrigen,   setFilterOrigen]   = useState(''); // '' | 'FACTURA_COMPRA' | 'MANUAL'

  // Modales
  const [showNuevo,     setShowNuevo]     = useState(false);
  const [detailMov,     setDetailMov]     = useState(null);
  const [editObsMov,    setEditObsMov]    = useState(null);
  const [deleteMov,     setDeleteMov]     = useState(null);

  // Paginación
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [movs, kpiData, prods, alms, inv] = await Promise.all([
        movimientosStockService?.getAll({
          search,
          productoId:     filterProducto    || null,
          tipo:           filterTipo        || null,
          almacenId:      filterAlmacen     || null,
          dateFrom:       filterDesde       || null,
          dateTo:         filterHasta       || null,
          referenciaTipo: filterOrigen      || null,
        }),
        movimientosStockService?.getKPIs(),
        productosService?.getAll(),
        almacenesService?.getAll(),
        inventarioService?.getAll(),
      ]);
      setMovimientos(movs);
      setKpis(kpiData);
      setProductos(prods);
      setAlmacenes(alms);
      setInventario(inv);
      // No resetear página al refrescar para mantener posición
    } catch (err) {
      setError(err?.message || 'Error al cargar movimientos');
    } finally {
      setLoading(false);
    }
  }, [search, filterProducto, filterTipo, filterAlmacen, filterDesde, filterHasta, filterOrigen]);

  useEffect(() => { loadData(); }, [loadData]);

  // Guardar nuevo movimiento — cerrar modal solo tras éxito
  const handleSave = async (movimiento) => {
    await movimientosStockService?.create(movimiento);
    // Refrescar movimientos e inventario sin perder filtros
    await loadData();
  };

  // Editar observaciones
  const handleEditObs = async (id, observaciones) => {
    await movimientosStockService?.updateObservaciones(id, observaciones);
    await loadData();
  };

  // Eliminar (solo admin, sin reversión automática)
  const handleDelete = async (id) => {
    await movimientosStockService?.delete(id);
    await loadData();
  };

  const clearFilters = () => {
    setSearch('');
    setFilterProducto('');
    setFilterTipo('');
    setFilterAlmacen('');
    setFilterDesde('');
    setFilterHasta('');
    setFilterOrigen('');
    setPage(1);
  };

  const hasFilters = search || filterProducto || filterTipo || filterAlmacen || filterDesde || filterHasta || filterOrigen;
  const paged      = movimientos?.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(movimientos?.length / pageSize);

  const kpiCards = [
    { label: 'Total Mes',     value: kpis?.totalMes     ?? '—', icon: 'ArrowUpDown',    color: GOLD  },
    { label: 'Hoy',           value: kpis?.hoy          ?? '—', icon: 'Clock',          color: BLUE  },
    { label: 'Entradas Mes',  value: typeof kpis?.entradasMes === 'number' ? fmt(kpis?.entradasMes, 0) : '—', icon: 'ArrowDownCircle', color: ACCENT },
    { label: 'Salidas Mes',   value: typeof kpis?.salidasMes  === 'number' ? fmt(kpis?.salidasMes,  0) : '—', icon: 'ArrowUpCircle',   color: RED   },
  ];

  const selectSt = { background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.70)' };

  // Extract factura reference from observaciones
  const getFacturaRef = (m) => {
    if (m?.referencia_tipo === 'FACTURA_COMPRA' && m?.observaciones) {
      const match = m?.observaciones?.match(/factura\s+([^\s—\-]+)/i);
      return match?.[1] || null;
    }
    return null;
  };

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* ── Modales ── */}
      {showNuevo && (
        <MovimientoFormModal
          productos={productos}
          almacenes={almacenes}
          inventario={inventario}
          productoPreseleccionado={filterProducto}
          onSave={handleSave}
          onClose={() => setShowNuevo(false)}
        />
      )}
      {detailMov && <MovimientoDetailModal mov={detailMov} onClose={() => setDetailMov(null)} />}
      {editObsMov && <EditObservacionesModal mov={editObsMov} onSave={handleEditObs} onClose={() => setEditObsMov(null)} />}
      {deleteMov && <ConfirmDeleteModal mov={deleteMov} onConfirm={handleDelete} onClose={() => setDeleteMov(null)} />}
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30` }}>
              <Icon name="ArrowUpDown" size={18} style={{ color: GOLD }} />
            </div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Movimientos de Stock</h1>
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-12">Historial completo · tienda.movimientos_stock</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowNuevo(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum transition-all"
            style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30`, color: GOLD }}>
            <Icon name="Plus" size={15} />Nuevo Movimiento
          </button>
          <button onClick={loadData}
            className="p-2.5 rounded-xl transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            <Icon name="RefreshCw" size={14} />
          </button>
        </div>
      </div>
      {/* ── KPIs ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {kpiCards?.map((kpi, i) => (
          <div key={i} className="rounded-xl p-4 flex items-center gap-3"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${kpi?.color}15`, border: `1px solid ${kpi?.color}25` }}>
              <Icon name={kpi?.icon} size={16} style={{ color: kpi?.color }} />
            </div>
            <div>
              <p className="font-oranienbaum font-bold text-white text-xl">{loading ? '...' : kpi?.value}</p>
              <p className="font-oranienbaum text-white/45 text-xs">{kpi?.label}</p>
            </div>
          </div>
        ))}
      </div>
      {/* ── Filtros ── */}
      <div className="rounded-2xl p-4 mb-4 space-y-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="relative">
          <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input type="text"
            placeholder="Buscar por producto, referencia, observaciones..."
            value={search} onChange={e => { setSearch(e?.target?.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm font-oranienbaum text-white/80 placeholder-white/25 outline-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select value={filterProducto} onChange={e => { setFilterProducto(e?.target?.value); setPage(1); }}
            className="px-3 py-2 rounded-xl text-sm font-oranienbaum outline-none" style={selectSt}>
            <option value="">Todos los productos</option>
            {productos?.map(p => <option key={p?.id} value={p?.id}>{p?.nombre}</option>)}
          </select>
          <select value={filterTipo} onChange={e => { setFilterTipo(e?.target?.value); setPage(1); }}
            className="px-3 py-2 rounded-xl text-sm font-oranienbaum outline-none" style={selectSt}>
            <option value="">Todos los tipos</option>
            {TIPOS_MOVIMIENTO?.map(t => <option key={t} value={t}>{TIPO_CONFIG?.[t]?.label}</option>)}
          </select>
          <select value={filterAlmacen} onChange={e => { setFilterAlmacen(e?.target?.value); setPage(1); }}
            className="px-3 py-2 rounded-xl text-sm font-oranienbaum outline-none" style={selectSt}>
            <option value="">Todos los almacenes</option>
            {almacenes?.map(a => <option key={a?.id} value={a?.id}>{a?.nombre}</option>)}
          </select>
          <select value={filterOrigen} onChange={e => { setFilterOrigen(e?.target?.value); setPage(1); }}
            className="px-3 py-2 rounded-xl text-sm font-oranienbaum outline-none" style={selectSt}>
            <option value="">Todos los orígenes</option>
            <option value="FACTURA_COMPRA">Compras (facturas)</option>
          </select>
          <input type="date" value={filterDesde} onChange={e => { setFilterDesde(e?.target?.value); setPage(1); }}
            className="px-3 py-2 rounded-xl text-sm font-oranienbaum outline-none" style={selectSt} title="Desde" />
          <input type="date" value={filterHasta} onChange={e => { setFilterHasta(e?.target?.value); setPage(1); }}
            className="px-3 py-2 rounded-xl text-sm font-oranienbaum outline-none" style={selectSt} title="Hasta" />
          {hasFilters && (
            <button onClick={clearFilters}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum"
              style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)', color: RED }}>
              <Icon name="X" size={12} />Limpiar
            </button>
          )}
        </div>
      </div>
      {/* ── Error ── */}
      {error && (
        <div className="mb-4 p-4 rounded-xl flex items-center gap-3"
          style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
          <Icon name="AlertCircle" size={16} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-sm">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400/60 hover:text-red-400"><Icon name="X" size={14} /></button>
        </div>
      )}
      {/* ── Tabla ── */}
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="px-4 py-3 flex items-center justify-between"
          style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <span className="font-oranienbaum text-white/50 text-xs">{movimientos?.length} movimientos</span>
          {filterOrigen === 'FACTURA_COMPRA' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-oranienbaum"
              style={{ background: 'rgba(96,165,250,0.12)', color: BLUE, border: '1px solid rgba(96,165,250,0.25)' }}>
              <Icon name="Filter" size={10} />Filtrado: Compras
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['Fecha', 'Producto', 'Tipo', 'Cantidad', 'Coste Unit.', 'Coste Total', 'Almacén Destino', 'Referencia Factura', 'Usuario', 'Observaciones', 'Acciones']?.map(col => (
                  <th key={col} className="px-3 py-3 text-left whitespace-nowrap">
                    <span className="font-oranienbaum text-white/40 text-xs uppercase tracking-wider">{col}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#D6EF4E] animate-spin" />
                    <p className="font-oranienbaum text-white/40 text-sm">Cargando movimientos...</p>
                  </div>
                </td></tr>
              ) : paged?.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                      style={{ background: `${GOLD}08`, border: `1px solid ${GOLD}15` }}>
                      <Icon name="ArrowUpDown" size={24} style={{ color: `${GOLD}40` }} />
                    </div>
                    <p className="font-oranienbaum text-white/40 text-sm">No hay movimientos registrados</p>
                    <button onClick={() => setShowNuevo(true)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-oranienbaum transition-all"
                      style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30`, color: GOLD }}>
                      <Icon name="Plus" size={13} />Registrar primer movimiento
                    </button>
                  </div>
                </td></tr>
              ) : paged?.map((m, i) => {
                const facturaRef = getFacturaRef(m);
                const costeTotal = typeof m?.coste_unitario === 'number' && typeof m?.cantidad === 'number'
                  ? m?.coste_unitario * m?.cantidad : null;
                return (
                  <tr key={m?.id}
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}
                    className="hover:bg-white/[0.03] transition-colors">
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className="font-oranienbaum text-white/50 text-xs">{fmtDate(m?.created_at)}</span>
                    </td>
                    <td className="px-3 py-3">
                      <div>
                        <p className="font-oranienbaum text-white/85 text-sm">{m?.producto?.nombre || '—'}</p>
                        {m?.producto?.codigo_interno && <p className="font-oranienbaum text-white/35 text-xs">{m?.producto?.codigo_interno}</p>}
                      </div>
                    </td>
                    <td className="px-3 py-3"><TipoBadge tipo={m?.tipo_movimiento} /></td>
                    <td className="px-3 py-3">
                      <span className="font-oranienbaum text-white font-semibold text-sm">{fmt(m?.cantidad, 3)}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-oranienbaum text-white/70 text-xs">{fmtEur(m?.coste_unitario)}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-oranienbaum text-white/70 text-xs">{fmtEur(costeTotal)}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-oranienbaum text-white/50 text-xs">{m?.almacen_destino?.nombre || m?.almacen_origen?.nombre || '—'}</span>
                    </td>
                    <td className="px-3 py-3">
                      {m?.referencia_tipo === 'FACTURA_COMPRA' ? (
                        <div>
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-oranienbaum"
                            style={{ background: 'rgba(96,165,250,0.12)', color: BLUE, border: '1px solid rgba(96,165,250,0.20)' }}>
                            <Icon name="FileText" size={9} />Compra
                          </span>
                          {facturaRef && <p className="font-oranienbaum text-white/40 text-[10px] mt-0.5">{facturaRef}</p>}
                        </div>
                      ) : m?.referencia_tipo ? (
                        <span className="font-oranienbaum text-white/40 text-xs">{m?.referencia_tipo}</span>
                      ) : (
                        <span className="text-white/20 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-oranienbaum text-white/50 text-xs">{m?.usuario || '—'}</span>
                    </td>
                    <td className="px-3 py-3 max-w-[150px]">
                      <span className="font-oranienbaum text-white/40 text-xs truncate block">{m?.observaciones || '—'}</span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        {/* Ver detalle */}
                        <button onClick={() => setDetailMov(m)} title="Ver detalle"
                          className="p-1.5 rounded-lg text-white/30 hover:text-blue-400 transition-colors"
                          style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                          <Icon name="Eye" size={12} />
                        </button>
                        {/* Editar observaciones */}
                        <button onClick={() => setEditObsMov(m)} title="Editar observaciones"
                          className="p-1.5 rounded-lg text-white/30 hover:text-yellow-400 transition-colors"
                          style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                          <Icon name="FileEdit" size={12} />
                        </button>
                        {/* Eliminar — solo admin, sin reversión automática */}
                        {isAdmin && (
                          <button onClick={() => setDeleteMov(m)} title="Eliminar movimiento"
                            className="p-1.5 rounded-lg text-white/30 hover:text-red-400 transition-colors"
                            style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                            <Icon name="Trash2" size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        <div className="px-4 py-3 flex items-center justify-between"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <span className="font-oranienbaum text-white/30 text-xs">
            {movimientos?.length === 0
              ? '0 resultados'
              : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, movimientos?.length)} de ${movimientos?.length}`}
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors disabled:opacity-30"
              style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
              <Icon name="ChevronLeft" size={14} />
            </button>
            <span className="px-3 py-1 rounded-lg font-oranienbaum text-xs" style={{ background: `${GOLD}15`, color: GOLD }}>{page}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors disabled:opacity-30"
              style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
              <Icon name="ChevronRight" size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TiendaMovimientos;
